import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckIcon } from 'lucide-react';
import {
  Tool,
  ToolHeader,
  ToolContent,
} from '@cotera/client/v0/components/ai-elements/tool';
import { Button } from '@cotera/client/v0/components/ui/button';
import { useTenantedClient } from '@cotera/client/app/stores/org';
import { toast } from '@cotera/client/app/components/ui';
import type { ToolRendererProps } from './index';
import type {
  HijackerWizardContext,
  InputHijackerProps,
} from '../input-hijackers';
import { translateToolName } from '../../../app/v1/components/translate-tool-name';
import { useSuspensfulToolInfo } from '../../../app/hooks/use-tools-info';
import { useAgentDisplayNameResolver } from '../../../app/hooks/use-agents-display-names';
import {
  FolderPickerAskView,
  SELECT_FOLDER_DEFAULT_LABELS,
} from '../../../app/v1/resources/folder-picker-ask-view';
import { cn } from '@cotera/client/v0/lib/utils';

type SelectFolderInput = {
  prompt?: string;
};

type SelectFolderOutput = {
  folderId: string;
};

/**
 * Renders coco/select-folder: same folder browser as Move to folder, shown in the
 * chat input hijacker (or in-thread when hijacking is unavailable).
 */
export const SelectFolderRenderer: React.FC<
  ToolRendererProps & InputHijackerProps
> = ({
  part,
  chatModel,
  readonly,
  embedded = false,
  conversationId,
  wizard,
}) => {
  const input = (part.input ?? {}) as SelectFolderInput;
  const output = part.getOutput<SelectFolderOutput>();
  const showForm = part.isIncomplete() && readonly !== true;
  const effectiveConversationId = conversationId ?? chatModel.id;

  const toolInfo = useSuspensfulToolInfo(part.toolKey().toString());
  const getAgentDisplayName = useAgentDisplayNameResolver();
  const toolName =
    getAgentDisplayName(part.toolKey().toString()) ??
    toolInfo?.name ??
    part.toolKey().displayName();

  if (showForm) {
    const promptText =
      typeof input.prompt === 'string' && input.prompt.trim() !== ''
        ? input.prompt.trim()
        : SELECT_FOLDER_DEFAULT_LABELS.pageTitle;

    return (
      <SelectFolderForm
        part={part}
        chatModel={chatModel}
        embedded={embedded}
        conversationId={effectiveConversationId}
        promptText={promptText}
        toolName={toolName}
        wizard={wizard}
      />
    );
  }

  if (
    part.isCompleted() &&
    output?.folderId !== undefined &&
    output.folderId !== ''
  ) {
    return (
      <div className="flex items-center gap-1.5 text-sm italic text-muted-foreground py-1">
        <CheckIcon className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
        <span>
          Folder:{' '}
          <span className="text-foreground font-mono text-xs">
            {output.folderId}
          </span>
        </span>
      </div>
    );
  }

  return null;
};

const SelectFolderForm: React.FC<{
  part: ToolRendererProps['part'];
  chatModel: ToolRendererProps['chatModel'];
  embedded?: boolean;
  conversationId: string;
  promptText: string;
  toolName: string;
  wizard?: HijackerWizardContext;
}> = ({
  part,
  chatModel,
  embedded = false,
  conversationId,
  promptText,
  toolName,
  wizard,
}) => {
  const client = useTenantedClient();
  const [submitting, setSubmitting] = useState(false);
  const [pendingFolderId, setPendingFolderId] = useState<string | null>(null);
  const deferredSelect = wizard !== undefined;

  const labels = useMemo(
    () => ({
      ...SELECT_FOLDER_DEFAULT_LABELS,
      pageTitle: promptText,
    }),
    [promptText]
  );

  const submitFolderId = useCallback(
    async (folderId: string) => {
      setSubmitting(true);
      try {
        const result = await client.resource.chat.runInterruptedTool({
          conversationId,
          toolCallId: part.toolCallId,
          model: chatModel.model,
          input: { folderId },
        });
        if (result.isErr()) {
          toast.error('Failed to submit folder selection');
        }
      } catch {
        toast.error('Failed to submit folder selection');
      } finally {
        setSubmitting(false);
      }
    },
    [client, conversationId, chatModel.model, part.toolCallId]
  );

  const handleCancel = useCallback(async () => {
    setSubmitting(true);
    try {
      const result = await client.resource.chat.runInterruptedTool({
        conversationId,
        toolCallId: part.toolCallId,
        model: chatModel.model,
        input: { cancelled: true },
      });
      if (result.isErr()) {
        toast.error('Failed to cancel');
      }
    } catch {
      toast.error('Failed to cancel');
    } finally {
      setSubmitting(false);
    }
  }, [client, conversationId, chatModel.model, part.toolCallId]);

  const handleSelectSubmit = useCallback(async () => {
    if (pendingFolderId === null || pendingFolderId === '') {
      return;
    }
    await submitFolderId(pendingFolderId);
  }, [pendingFolderId, submitFolderId]);

  useEffect(() => {
    if (wizard === undefined) {
      return;
    }
    wizard.setCanComplete(pendingFolderId !== null && pendingFolderId !== '');
  }, [wizard, pendingFolderId]);

  const skipButton = useMemo(
    () => (
      <Button
        size="sm"
        variant="outline"
        onClick={() => void handleCancel()}
        disabled={submitting}
      >
        Cancel
      </Button>
    ),
    [handleCancel, submitting]
  );

  const submitButton = useMemo(
    () => (
      <Button
        size="sm"
        variant="default"
        onClick={() => void handleSelectSubmit()}
        disabled={
          submitting || pendingFolderId === null || pendingFolderId === ''
        }
      >
        Select
      </Button>
    ),
    [handleSelectSubmit, submitting, pendingFolderId]
  );

  useEffect(() => {
    if (wizard === undefined) {
      return;
    }
    wizard.registerSkipButton(skipButton);
    wizard.registerSubmitButton(submitButton);
    return () => {
      wizard.registerSkipButton(null);
      wizard.registerSubmitButton(null);
    };
  }, [wizard, skipButton, submitButton]);

  return (
    <div
      className={cn(
        'flex min-h-0 w-full min-w-0 flex-col',
        embedded === true && 'px-1'
      )}
    >
      <Tool
        defaultOpen
        className="mb-0 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
      >
        <ToolHeader
          state="input-available"
          title={translateToolName(toolName, 'waiting')}
          type={part.toolPart.type}
        />
        <ToolContent className="min-h-0 flex-1 overflow-hidden p-0 data-[state=open]:flex data-[state=open]:min-h-0 data-[state=open]:flex-1 data-[state=open]:flex-col">
          <div
            className={cn(
              'flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden',
              submitting && 'pointer-events-none opacity-60'
            )}
          >
            <FolderPickerAskView
              targetResource={null}
              onRespond={async (params) => {
                await submitFolderId(params.folderId);
              }}
              onCancel={() => {
                void handleCancel();
              }}
              labels={labels}
              onPickFolder={
                deferredSelect === true
                  ? (folderId) => {
                      setPendingFolderId(folderId);
                    }
                  : undefined
              }
              highlightFolderId={
                deferredSelect === true ? pendingFolderId : null
              }
              showCancelButton={deferredSelect !== true}
            />
          </div>
        </ToolContent>
      </Tool>
    </div>
  );
};
